import { BaseModel, BaseWithId, HasId } from '@app/shared/models/base';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import { ParticipantTypes } from '@app/shared/models/participant-type';
import { BaseWithIdList } from '@app/shared/models/list';
import { DisplayName } from '@app/shared/components/select/base-select-with-id.component';
import { DistributionJob } from '@app/shared/models/distribution-doc-models';


export enum WorkflowTargets {
    SMP_CASE = 'smp_case'
}

export enum CaseWorkflowSections {
    DEFAULTS = 'defaults'
}

export enum CaseWorkflowSettingsTypes {
    PARTICIPANTS = 'participants',
    DISTRIBUTIONS = 'distribution_jobs'
}

export abstract class Workflow extends BaseWithId
                               implements ActionListItem {

    public active: boolean;
    public client_id: string;
    public name: string;
    public sort_order: number;
    public target: WorkflowTargets;

    get actionListDisplayName(): string {
        return this.name;
    }

    public abstract getSections(): any;
    public abstract getDefaultSectionName(): string;
    public abstract getSection(section: string): WorkflowSection;
    public abstract setSection(section: string, json?: any): void;

    protected constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {

        });
    }

    public static createSubType(json?: any): Workflow {
        const subType = Workflow.getSubTypeConstructor(json);
        return new subType(json);
    }

    public static getSubTypeConstructor(json?: any): new (json?: any) => Workflow {
        switch (json.target) {
            case WorkflowTargets.SMP_CASE: {
                return CaseWorkflow;
            }
            default: {
                return CaseWorkflow;
            }
        }
    }
}

export class CaseWorkflow extends Workflow {

    public defaults: CaseWorkflowSectionDefaults;

    constructor(json?: any) {
        super(json);
    }

    public getSections(): typeof CaseWorkflowSections {
        return CaseWorkflowSections;
    }

    public getDefaultSectionName(): string {
        return CaseWorkflowSections.DEFAULTS;
    }

    public getSection(section: CaseWorkflowSections): WorkflowSection {
        switch (section) {
            case CaseWorkflowSections.DEFAULTS: {
                return this.defaults;
            }
            default: {
                return null;
            }
        }
    }

    public setSection(section: CaseWorkflowSections, json?: any): void {
        switch (section) {
            case CaseWorkflowSections.DEFAULTS: {
                this.defaults = new CaseWorkflowSectionDefaults(json);
                break;
            }
            default: {
                break;
            }
        }
    }
}

export abstract class WorkflowSection extends BaseModel
                                      implements HasId {

    get id(): string {
        return this.workflow_id;
    }

    public workflow_id: string;
    public section: string;

}

export class CaseWorkflowSection extends WorkflowSection {

    public section: CaseWorkflowSections;

    constructor(json?: any) {
        super(json);
    }
}

export class CaseWorkflowSectionDefaults extends CaseWorkflowSection {

    public participants: BaseWithIdList<WorkflowParticipant>;
    public distribution_jobs: BaseWithIdList<DistributionJob>;

    constructor(json?: any) {
        super(json);
        this.section = CaseWorkflowSections.DEFAULTS;
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            participants: new BaseWithIdList(WorkflowParticipant, json && json.participants),
            distribution_jobs: new BaseWithIdList(DistributionJob, json && json.distribution_jobs.map(job => DistributionJob.createSubTypeByChannel(job)))
        });
    }
}

export class WorkflowParticipant extends BaseWithId
                                 implements DisplayName, ActionListItem {

    public client_id: string;
    public type: string[];
    public workflow_id: string;

    get displayName() {
        return this.actionListDisplayName;
    }

    set displayName(displayName: string) {
        this.actionListDisplayName = displayName;
    }

    public actionListDisplayName: string;

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            type: json && json.type ? [...json.type] : new Array<string>()
        });
    }

    public getMyTypeTranslationKeys(): TranslationEntry[] {
        return this.type.map((type: string) => {
            const translationEntry = ParticipantTypes.getTypeByKey(type) || { key: type, trans: type };
            return translationEntry;
        });
    }

    public isOfType(type: string): boolean {
        return this.type.indexOf(type) > -1;
    }

}
